import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getApiBase } from "../config";
import { getToken } from "../auth";

const API_BASE_URL = getApiBase();

type AdminStats = {
  total_organizations?: number;
  stats?: {
    total_revenue?: number;
    active_leases?: number;
  };
  organizations?: Array<{
    id: string;
    name: string;
    owner_email?: string;
    token_balance?: number;
    status?: string;
  }>;
};

const AdminDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchStats = async () => {
    const token = getToken();
    if (!token) {
      navigate("/login");
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/admin/stats`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else if (response.status === 401) {
        alert("Session expired. Please login again.");
        localStorage.clear();
        navigate("/login");
      } else {
        const err = await response.json().catch(() => ({}));
        console.error("Failed to fetch stats", err);
      }
    } catch (error) {
      console.error("Failed to fetch stats", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  if (loading) {
    return (
      <div className="p-8 text-slate-900 dark:text-white">
        Loading Dashboard...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 p-8 font-sans">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
              Super Admin
            </h1>
            <p className="text-slate-500 dark:text-slate-400">
              System Overview & Financials
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate("/admin/organizations/new")}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium shadow-sm transition"
            >
              + New Organization
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          <div className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
              Total Organizations
            </p>
            <h3 className="text-3xl font-bold text-slate-900 dark:text-white mt-2">
              {stats?.total_organizations || 0}
            </h3>
          </div>

          <div className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
              Total Revenue (Tokens)
            </p>
            <h3 className="text-3xl font-bold text-green-600 mt-2">
              {stats?.stats?.total_revenue || 0}
            </h3>
          </div>

          <div className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
            <p className="text-sm font-medium text-slate-500 dark:text-slate-400">
              Active Leases
            </p>
            <h3 className="text-3xl font-bold text-blue-600 mt-2">
              {stats?.stats?.active_leases || 0}
            </h3>
          </div>
        </div>

        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700 overflow-hidden">
          <div className="p-6 border-b border-slate-200 dark:border-slate-700">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              Active Organizations
            </h2>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-slate-600 dark:text-slate-300">
              <thead className="bg-slate-50 dark:bg-slate-700/50 text-slate-900 dark:text-white font-medium">
                <tr>
                  <th className="px-6 py-4">Name</th>
                  <th className="px-6 py-4">Owner Email</th>
                  <th className="px-6 py-4">Balance</th>
                  <th className="px-6 py-4">Status</th>
                  <th className="px-6 py-4 text-right">Actions</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {stats?.organizations?.map((org) => (
                  <tr
                    key={org.id}
                    className="hover:bg-slate-50 dark:hover:bg-slate-700/50 transition"
                  >
                    <td className="px-6 py-4 font-medium text-slate-900 dark:text-white">
                      {org.name}
                    </td>
                    <td className="px-6 py-4">{org.owner_email || "-"}</td>
                    <td className="px-6 py-4 font-mono">
                      {org.token_balance ?? 0}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          (org.status || "").toLowerCase() === "active"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        }`}
                      >
                        {org.status || "Unknown"}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => navigate(`/admin/orgs/${org.id}`)}
                        className="text-blue-600 hover:text-blue-500 font-medium"
                      >
                        Manage
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {(!stats?.organizations || stats.organizations.length === 0) && (
              <div className="p-8 text-center text-slate-400">
                No organizations found. Create one!
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
